import React, { useContext, useEffect, useState } from 'react';
import * as yup from 'yup';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import useForm, { proxiedPropertiesOf } from '@hooks/useForm/useForm.tsx';
import RingilTextArea from '@components/framework/input/RingilTextArea.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import EditableHeading3 from '@components/ringil3/Heading/EditableHeading3.tsx';
import SvgNote from '@icons/note.svg?react';

interface ComponentProps {
  ownerCompanyId: string;
  partnerCompanyId: string;
  readOnly?: boolean;
  // leading separator shown only when the note section actually renders (card layout)
  showTopDivider?: boolean;
}

interface FormData {
  internalNote: string;
}

const InputInternalNote: React.FC<ComponentProps> = ({ ownerCompanyId, partnerCompanyId, readOnly, showTopDivider }) => {
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const { getCompanyPartnerInfo, createOrUpdateCompanyPartnerInfo } = useContext(ApiContext);
  const [editMode, setEditMode] = useState(false);
  const { tr } = useTranslationLgs();
  const names = proxiedPropertiesOf<FormData>();

  const form = useForm<FormData>(
    yup.object().shape({}),
    { internalNote: '' },
    d => {
      save(d);
    },
    false,
    false,
  );

  const loadNote = () => {
    form.setFieldValue(names.internalNote, '');
    if (partnerCompanyId) {
      getCompanyPartnerInfo(partnerCompanyId, data => {
        if (data != null) {
          form.setFieldValue(names.internalNote, data.partnerNote);
        }
      });
    }
  };

  useEffect(() => {
    loadNote();
  }, [partnerCompanyId]);

  const save = (d: FormData) => {
    createOrUpdateCompanyPartnerInfo({ partnerNote: form.values.internalNote, partnerCompanyId }, () => {
      setEditMode(false);
    });
  };

  const cancel = () => {
    loadNote();
    setEditMode(false);
  };

  if (loggedUser.companyId !== ownerCompanyId) return null;
  if (readOnly && !form.values.internalNote) return null;

  return (
    <div className="flex flex-col gap-2">
      {showTopDivider && <div className="h-px bg-gray-200 my-2" />}
      <EditableHeading3
        title={tr('InputInternalNote.internalNote', 'Internal note')}
        icon={<SvgNote className="text-content-secondary" />}
        titleVariant={'h4'}
        onEditClick={() => setEditMode(true)}
        editable={!readOnly && !editMode}
      />
      {editMode && !readOnly ? (
        <div className="flex flex-col gap-2">
          <RingilTextArea value={form.values.internalNote} onChange={v => form.setFieldValue(names.internalNote, v)} rows={3} />
          <div className="flex items-center gap-2">
            <RingilButton mode={'filled-rectangle'} size={'small'} label={tr('InputInternalNote.save', 'Save')} onClick={() => form.validateAndSend()} />
            <RingilButton mode={'outlined-rectangle'} size={'small'} label={tr('generic.cancel', 'Cancel')} onClick={cancel} />
          </div>
        </div>
      ) : (
        form.values.internalNote && (
          <div className="text-[15px] leading-[normal] text-content-primary whitespace-pre-wrap [overflow-wrap:anywhere]">{form.values.internalNote}</div>
        )
      )}
    </div>
  );
};

export default InputInternalNote;
